import type { CadDocument } from '../model/document';

/**
 * 파일 포맷 어댑터 (작업지시서 P3-01)
 *
 * ★ 이 인터페이스가 존재하는 이유
 *   DWG 쓰기는 순수 JS 로 불가능하다 (`고도화_제안_v2.md` 3.1절).
 *   엔진 선택(LibreDWG / ODA / APS)이 라이선스 상황에 따라 바뀔 수 있으므로,
 *   교체 비용을 **구현체 파일 하나** 로 묶어 둔다.
 *
 *   현재 결정(2026-07-27): LibreDWG(GPL-3.0)로 착수.
 *   ODA 라이선스가 확정되면 `worker/engines/oda.ts` 로 교체한다.
 *
 * 실행 위치가 다르다:
 *   · DxfAdapter    — 브라우저와 워커 양쪽 (순수 TS)
 *   · SvgAdapter    — 브라우저
 *   · LibreDwgAdapter / OdaAdapter — 워커 컨테이너 전용 (네이티브 바이너리 필요)
 */

export type CadFormat = 'dxf' | 'dwg' | 'dwt' | 'svg' | 'pdf' | 'png' | 'jpg';

/** 변환 중 발생한 손실·대체를 사용자에게 그대로 보여주기 위한 기록 */
export interface ConversionWarning {
  /** 기계 판독용 코드 — UI 가 분류·집계에 쓴다 */
  code:
    | 'unsupported_entity'
    | 'font_substituted'
    | 'xref_missing'
    | 'dynamic_block_flattened'
    | 'precision_rounded'
    | 'layer_created'
    | 'binary_dxf'
    | 'parse_recovered'
    /** 타원·스플라인을 다각형(폴리선)으로 근사했다 — 매끄러운 곡선 자체는 아니다 */
    | 'curve_approximated'
    /** PDF 안의 래스터 이미지는 벡터 변환 대상이 아니라 건너뛰었다 */
    | 'raster_skipped';
  /** 사람이 읽는 설명 */
  message: string;
  /** 해당 엔티티 종류나 이름 */
  subject?: string;
  /** 같은 경고가 몇 번 발생했는지 */
  count?: number;
}

export interface ReadResult {
  doc: CadDocument;
  warnings: ConversionWarning[];
}

export interface WriteOptions {
  /** DXF 버전. 기본 AC1021(2007) — 호환성이 가장 넓다. */
  version?: 'AC1015' | 'AC1018' | 'AC1021' | 'AC1024' | 'AC1032';
  /** 도면 단위 (DXF $INSUNITS). 4 = mm */
  insUnits?: number;
}

export interface WriteResult {
  data: Uint8Array;
  warnings: ConversionWarning[];
}

export interface CadFormatAdapter {
  readonly name: string;
  canRead(fmt: CadFormat): boolean;
  canWrite(fmt: CadFormat): boolean;
  read(buf: ArrayBuffer, fmt: CadFormat): Promise<ReadResult>;
  write(doc: CadDocument, fmt: CadFormat, opt?: WriteOptions): Promise<WriteResult>;
}

/** 경고를 종류별로 합친다 — 같은 경고 500개를 그대로 보여주면 아무도 안 읽는다. */
export function collapseWarnings(list: ConversionWarning[]): ConversionWarning[] {
  const byKey = new Map<string, ConversionWarning>();
  for (const w of list) {
    const key = `${w.code}:${w.subject ?? ''}`;
    const prev = byKey.get(key);
    if (prev) {
      prev.count = (prev.count ?? 1) + (w.count ?? 1);
    } else {
      byKey.set(key, { ...w, count: w.count ?? 1 });
    }
  }
  return [...byKey.values()];
}

export const FORMAT_LABEL: Record<CadFormat, string> = {
  dxf: 'DXF (도면 교환 형식)',
  dwg: 'DWG (AutoCAD 원본)',
  dwt: 'DWT (AutoCAD 템플릿)',
  svg: 'SVG (벡터 이미지)',
  pdf: 'PDF (인쇄용)',
  png: 'PNG (이미지)',
  jpg: 'JPG (이미지)',
};

/** 파일명에서 포맷 추정 */
export function formatOf(filename: string): CadFormat | null {
  const ext = filename.toLowerCase().split('.').pop() ?? '';
  if (ext === 'jpeg') return 'jpg';
  return (['dxf', 'dwg', 'dwt', 'svg', 'pdf', 'png', 'jpg'] as CadFormat[]).includes(
    ext as CadFormat
  )
    ? (ext as CadFormat)
    : null;
}
